/**
 * Migration utilities for localStorage to database migration
 */

import type { DailyEntry as LocalStorageDailyEntry } from "./storage"

const STORAGE_KEY = "inner-scale-entries"

export interface MigrationValidationResult {
  isValid: boolean
  entries: LocalStorageDailyEntry[]
  errors: string[]
  warnings: string[]
}

/**
 * Detects and validates localStorage data for migration
 * Returns validation result with entries, errors, and warnings
 */
export function detectLocalStorageData(): MigrationValidationResult {
  const result: MigrationValidationResult = {
    isValid: false,
    entries: [],
    errors: [],
    warnings: [],
  }

  // Check if we're in browser environment
  if (typeof window === "undefined") {
    result.errors.push("Not in browser environment")
    return result
  }

  // Check if localStorage is available
  try {
    if (!window.localStorage) {
      result.errors.push("localStorage is not available")
      return result
    }
  } catch (error) {
    result.errors.push("localStorage access denied")
    return result
  }

  // Try to get data from localStorage
  let rawData: string | null
  try {
    rawData = localStorage.getItem(STORAGE_KEY)
  } catch (error) {
    result.errors.push("Failed to read from localStorage")
    return result
  }

  // Check if data exists
  if (!rawData) {
    result.warnings.push("No localStorage data found")
    return result
  }

  // Try to parse JSON
  let parsedData: unknown
  try {
    parsedData = JSON.parse(rawData)
  } catch (error) {
    result.errors.push("Failed to parse localStorage data as JSON")
    return result
  }

  // Validate that it's an array
  if (!Array.isArray(parsedData)) {
    result.errors.push("localStorage data is not an array")
    return result
  }

  // Check if array is empty
  if (parsedData.length === 0) {
    result.warnings.push("localStorage contains empty array")
    return result
  }

  // Validate each entry
  const validEntries: LocalStorageDailyEntry[] = []
  const validZoneIds = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "R"]

  parsedData.forEach((entry, index) => {
    // Check if entry is an object
    if (typeof entry !== "object" || entry === null) {
      result.warnings.push(`Entry at index ${index} is not an object`)
      return
    }

    // Type assertion for validation
    const e = entry as Record<string, unknown>

    // Validate required fields
    if (typeof e.id !== "string" || !e.id) {
      result.warnings.push(`Entry at index ${index} missing valid id`)
      return
    }

    if (typeof e.date !== "string" || !e.date) {
      result.warnings.push(`Entry at index ${index} missing valid date`)
      return
    }

    if (typeof e.zoneId !== "string" || !e.zoneId) {
      result.warnings.push(`Entry at index ${index} missing valid zoneId`)
      return
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(e.date)) {
      result.warnings.push(`Entry at index ${index} has invalid date format: ${e.date}`)
      return
    }

    // Validate date is a real date
    const dateObj = new Date(e.date)
    if (isNaN(dateObj.getTime())) {
      result.warnings.push(`Entry at index ${index} has invalid date: ${e.date}`)
      return
    }

    // Validate zoneId
    if (!validZoneIds.includes(e.zoneId)) {
      result.warnings.push(`Entry at index ${index} has invalid zoneId: ${e.zoneId}`)
      return
    }

    // Validate note if present
    if (e.note !== undefined && typeof e.note !== "string") {
      result.warnings.push(`Entry at index ${index} has invalid note type`)
      return
    }

    // Entry is valid
    validEntries.push({
      id: e.id,
      date: e.date,
      zoneId: e.zoneId,
      note: e.note as string | undefined,
    })
  })

  // Check if we have any valid entries
  if (validEntries.length === 0) {
    result.errors.push("No valid entries found in localStorage")
    return result
  }

  // Success!
  result.isValid = true
  result.entries = validEntries

  // Add info about skipped entries
  const skippedCount = parsedData.length - validEntries.length
  if (skippedCount > 0) {
    result.warnings.push(`Skipped ${skippedCount} invalid entries`)
  }

  return result
}

/**
 * Checks if there is any migratable data in localStorage
 */
export function hasLocalStorageData(): boolean {
  const result = detectLocalStorageData()
  return result.isValid && result.entries.length > 0
}

/**
 * Gets the count of valid entries available for migration
 */
export function getLocalStorageEntryCount(): number {
  const result = detectLocalStorageData()
  return result.isValid ? result.entries.length : 0
}

/**
 * Clears localStorage data after successful migration
 * Returns true if successful, false otherwise
 */
export function clearLocalStorageData(): boolean {
  if (typeof window === "undefined") {
    return false
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
    return true
  } catch (error) {
    console.error("Failed to clear localStorage:", error)
    return false
  }
}
